import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {
  buildTeammateDisplayName,
  isBotTeammate,
} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {NewPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {PopoverCoordinator} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {RepositionPopover} from '../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {ThemeContextType} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {renderTeammateNameOrYou} from '../../helpers/teammateHelpers';
import {
  getFindTeammate,
  getFindTeammateByAlias,
  getIsUserVisitor,
  getUserId,
} from '../../store/data/teammates/teammatesSelectors';
import {TeammateCard} from './teammateCard';

/*
 * Props.
 */

export interface TeammateCardPreviewerProps {
  className?: string;
  panelId: NewPanelId;
  teammate: TeammateModel;
}

export interface TeammateCardPopoverProps {
  panelId: NewPanelId;
  /** Corresponds to the teammateId from `mentionsMap`. */
  teammateId: number | undefined;
  /** Should be the mention text: for older comments. */
  teammateAlias: string | undefined;
  sourceRef: React.RefObject<HTMLElement>;
  onRequestClose: () => void;
}

/*
 * Styles.
 */

const StyledDivWithMargin = styled.div`
  // Adding a bit or margin do that popover is not "glued" to the anchor.
  margin: 5px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: inline-block;
`;

const StyledNameSpan = styled.span<{$isActive?: boolean}>`
  &:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }

  ${(p) => maybeIsActive(p)}
`;

function maybeIsActive(p: {$isActive?: boolean} & ThemeContextType) {
  if (p.$isActive) {
    return css`
      color: ${p.theme.interactiveTextHover};
    `;
  }

  return '';
}

/*
 * Component.
 */

export const TeammateCardPreviewer: FC<PropsWithChildren<TeammateCardPreviewerProps>> = (props) => {
  const {className, teammate, panelId, children} = props;

  const isUserVisitor = useAppSelector(getIsUserVisitor);

  return (
    <PopoverCoordinator
      className={className}
      renderButton={(isOpen) => <ChromelessButton isOpen={isOpen}>{children}</ChromelessButton>}
      renderModal={(modalProps) => (
        <RepositionPopover placement="right" {...modalProps} isExclusive>
          <StyledDivWithMargin>
            <TeammateCard teammate={teammate} panelId={panelId} isUserVisitor={isUserVisitor} />
          </StyledDivWithMargin>
        </RepositionPopover>
      )}
    />
  );
};

export const TeammateCardPopover: FC<TeammateCardPopoverProps> = (props) => {
  const {teammateId, teammateAlias, panelId, sourceRef, onRequestClose} = props;

  const isUserVisitor = useAppSelector(getIsUserVisitor);
  const findTeammate = useAppSelector(getFindTeammate);
  const findTeammateByAlias = useAppSelector(getFindTeammateByAlias);
  // Resolve the teammate by Id if possible.
  const teammateById = teammateId ? findTeammate(teammateId) : undefined;

  // Default back to alias for older comments.
  const teammateByAlias = !teammateById && teammateAlias ? findTeammateByAlias(teammateAlias) : undefined;

  // Render the popover if we have a teammate.
  const teammate = teammateById || teammateByAlias;
  if (!teammate) {
    return null;
  }

  return (
    <RepositionPopover placement="right" isExclusive onRequestClose={onRequestClose} anchor={sourceRef}>
      <StyledDivWithMargin>
        <TeammateCard teammate={teammate} panelId={panelId} isUserVisitor={isUserVisitor} />
      </StyledDivWithMargin>
    </RepositionPopover>
  );
};

interface TeammateNamePreviewerProps extends TeammateCardPreviewerProps {
  shouldDisplayFullName?: boolean;
  shouldCapitalizeYou?: boolean;
}

export const TeammateNamePreviewer: FC<TeammateNamePreviewerProps> = ({
  teammate,
  panelId,
  shouldDisplayFullName,
  shouldCapitalizeYou,
}) => {
  const userId = useAppSelector(getUserId);
  const isUserVisitor = useAppSelector(getIsUserVisitor);
  const teammateName = shouldDisplayFullName
    ? buildTeammateDisplayName(teammate)
    : renderTeammateNameOrYou(userId, teammate, shouldCapitalizeYou);

  if (isBotTeammate(teammate)) {
    return teammateName;
  }

  return (
    <StyledPopoverCoordinator
      renderButton={(isOpen) => <StyledNameSpan $isActive={isOpen}>{teammateName}</StyledNameSpan>}
      renderModal={(modalProps) => (
        <RepositionPopover placement="right" {...modalProps} isExclusive>
          <StyledDivWithMargin>
            <TeammateCard teammate={teammate} panelId={panelId} isUserVisitor={isUserVisitor} />
          </StyledDivWithMargin>
        </RepositionPopover>
      )}
    />
  );
};
